import { COMPANY, CUSTOM_SECURITY, CUSTOM_SECURITY_ID_PREFIX, MODEL_PORTFOLIO, MODEL_PORTFOLIO_ID_PREFIX, } from 'NewAngular/constants/core.constants';
import { getSecurityUrlPrefixFromType } from 'NewAngular/utils/security-utils';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../../search/static/search/ts/features/base-autocomplete/components/base-autocomplete.component";
/**
 *
 * SymbolSwitcherComponent.
 *
 * Search bar to switch securities from the quote header.
 *
 * Required Attributes:
 *      @ searchType : Search type that sets the collection from where the search results will be fetched.
 *      @ securityType : Security type of the current page.
 *      @ securityUrlIdentifier: URL identifier with the ID of the security.
 */
export class SymbolSwitcherComponent {
    /**
     * Handler for the Symbol Switcher search bar. It updates the current URL based on the new security and the
     * current security page.
     *
     * @param result: AutocompleteResult from `ycn-base-autocomplete`.
     */
    submitHandler(result) {
        if (!result)
            return false;
        let url = window.location.href;
        // Before we do anything else, here we try to detect if we are currently at the main quote page of
        // the current security. If we are then we just redirect to the main quote page of the new security
        // since that new URL is already part of the autocompleter object.
        // This helps in scenarios where for like stocks the URL looks like /companies/AAPL but for model
        // portfolios it looks like /model_portfolios/P:332432/quote
        const endsInSecurityIDRe = RegExp(`(${this.securityUrlIdentifier}|/quote)$`);
        if (endsInSecurityIDRe.test(url)) {
            window.location.href = result.url;
            return true;
        }
        if (this.securityType && result.type !== this.securityType) {
            const oldUrlPrefix = getSecurityUrlPrefixFromType(this.securityType);
            const newUrlPrefix = getSecurityUrlPrefixFromType(result.type);
            url = url.replace(oldUrlPrefix, newUrlPrefix);
            // When switching from a company to other security types i.e. mutual fund, some tabs have extra url
            // slugs which doesn't allow the url to properly redirect to the quote page.
            if (this.securityType === COMPANY) {
                // Since other security types have a performance tab, we remove the extra url slug `price` or
                // `management` to properly redirect. For the others, we remove the extra url slugs which will
                // match with the respective security type's data view, and redirect to quote tab if it doesn't
                // exist.
                if (url.includes('/performance/management')) {
                    url = url.replace('/performance/management', '/performance');
                }
                else if (url.includes('/performance/price')) {
                    url = url.replace('/performance/price', '/performance');
                }
                else if (url.includes('/key_stats')) {
                    url = url.replace(/\/key_stats.*/, '/key_stats');
                }
                else if (url.includes('/financials')) {
                    url = url.replace(/\/financials.*/, '/financials');
                }
                else if (url.includes('/events')) {
                    url = url.replace(/\/events.*/, '/events');
                }
                else if (url.includes('/ratings')) {
                    url = url.replace(/\/ratings.*/, '/ratings');
                }
            }
            else if (url.includes('/holdings_overlap')) {
                url = url.replace(/\/holdings_overlap.*/, '/holdings_overlap');
            }
            else if (url.includes('/holdings')) {
                url = url.replace(/\/holdings.*/, '/holdings');
            }
        }
        let newSecurityIdentifier = result.search_id;
        if (result.type === MODEL_PORTFOLIO || result.type === CUSTOM_SECURITY) {
            newSecurityIdentifier = result.id.toString();
        }
        const securityRE = RegExp(`${this.securityUrlIdentifier}|${encodeURIComponent(this.securityUrlIdentifier)}`, 'g');
        if (url.includes('#')) {
            // Model Portfolios and Custom Securities are different because we use the model ID
            // in base part of the URL, not the security ID with the prefix like P:1234. However, Fundamental Chart and Scatter Plot
            // use the full MP or CS security ID in the URL query params that represent the app state, so when switching to an MP or CS
            // we need to make sure the security IDs in the URL state get replaced with the full MP or CS security ID. The same is true
            // when switching from an MP or CS to other security types. We need to replace the full MP or CS security ID in the URL state
            // with the new security ID.
            let [baseUrl, urlState] = url.split('#');
            baseUrl = baseUrl.replace(securityRE, newSecurityIdentifier);
            const securityTypeToPrefixMap = {
                [MODEL_PORTFOLIO]: MODEL_PORTFOLIO_ID_PREFIX,
                [CUSTOM_SECURITY]: CUSTOM_SECURITY_ID_PREFIX,
            };
            const oldSecurityIdPrefix = securityTypeToPrefixMap[this.securityType] ?? '';
            const oldSecurityIdWithPrefix = `${oldSecurityIdPrefix}${this.securityUrlIdentifier}`;
            const securityREWithPrefix = RegExp(`${oldSecurityIdWithPrefix}|${encodeURIComponent(oldSecurityIdWithPrefix)}`, 'g');
            urlState = urlState.replace(securityREWithPrefix, result.search_id);
            url = `${baseUrl}#${urlState}`;
        }
        else {
            url = url.replace(securityRE, newSecurityIdentifier);
        }
        window.location.href = url;
        return true;
    }
}
SymbolSwitcherComponent.ɵfac = function SymbolSwitcherComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || SymbolSwitcherComponent)(); };
SymbolSwitcherComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SymbolSwitcherComponent, selectors: [["ycn-symbol-switcher"]], inputs: { securityUrlIdentifier: "securityUrlIdentifier", securityType: "securityType" }, standalone: false, decls: 5, vars: 4, consts: [[1, "form-inline"], [1, "form-label", "form-label-thin"], [1, "input-group", "symbol-switcher"], [3, "submitHandler", "dropdownMenuClass", "placeholder", "searchType", "includeSearchIcon"]], template: function SymbolSwitcherComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "label", 1);
        i0.ɵɵtext(2, "Switch to:");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "div", 2)(4, "ycn-base-autocomplete", 3);
        i0.ɵɵlistener("submitHandler", function SymbolSwitcherComponent_Template_ycn_base_autocomplete_submitHandler_4_listener($event) { return ctx.submitHandler($event); });
        i0.ɵɵelementEnd()()();
    } if (rf & 2) {
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("dropdownMenuClass", "dropdown-menu-right")("placeholder", "Name")("searchType", "securities_with_calcs")("includeSearchIcon", true);
    } }, dependencies: [i1.BaseAutocompleteComponent], encapsulation: 2 });
